import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useUpdateUserStateItemMutation, useDeleteUserStateItemMutation } from '../redux/services/userStateApi';
import { setEntry, removeEntry } from '../redux/features/userState/userStateSlice';
import { normalizeUserStateVersion } from '../utils/user-state/normalize-user-state-version';
import type { ApplicationKitState, UserStateApplicationKitKey, UserStateEntry } from '../types/user-state';
import { useAuth } from './useAuth';

export function applicationKitItemKey(jobId: string): UserStateApplicationKitKey {
  return `applicationKit#${jobId}`;
}

/**
 * Read/write/delete access to the `applicationKit#{jobId}` userState bucket — the persisted
 * tailored CV + cover letter output for a job. Kits are account-only (no anonymous/local-only
 * fallback like saved jobs get): they contain real generated content tied to a paid LLM call,
 * so there's nothing meaningful to keep client-side-only for a signed-out user.
 */
export function useApplicationKitActions() {
  const dispatch = useAppDispatch();
  const userStateEntries = useAppSelector((s) => s.userState?.entries ?? {});
  const [updateUserStateItem] = useUpdateUserStateItemMutation();
  const [deleteUserStateItem] = useDeleteUserStateItemMutation();
  const { isAuthenticated } = useAuth();

  const getApplicationKit = useCallback(
    (jobId: string): ApplicationKitState | null => {
      const entry = userStateEntries[applicationKitItemKey(jobId)] as UserStateEntry | undefined;
      const data = entry?.data as ApplicationKitState | undefined;
      return data && Object.keys(data).length > 0 ? data : null;
    },
    [userStateEntries]
  );

  const saveApplicationKit = useCallback(
    async (jobId: string, kit: ApplicationKitState): Promise<boolean> => {
      if (!isAuthenticated) return false;

      const itemKey = applicationKitItemKey(jobId);
      const currentEntry = userStateEntries[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        const result = await updateUserStateItem({ itemKey, data: kit, version }).unwrap();
        dispatch(setEntry({ itemKey, entry: result }));
        return true;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[application-kit] Failed to persist kit', error);
        }
        return false;
      }
    },
    [dispatch, updateUserStateItem, userStateEntries, isAuthenticated]
  );

  const deleteApplicationKit = useCallback(
    async (jobId: string): Promise<boolean> => {
      const itemKey = applicationKitItemKey(jobId);
      const currentEntry = userStateEntries[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        await deleteUserStateItem({ itemKey, version: version > 0 ? version : undefined }).unwrap();
        dispatch(removeEntry(itemKey));
        return true;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[application-kit] Failed to delete kit', error);
        }
        return false;
      }
    },
    [dispatch, deleteUserStateItem, userStateEntries]
  );

  return { getApplicationKit, saveApplicationKit, deleteApplicationKit };
}
